// src/tabs/logements/atoms.jsx — Phase 2.E.1 (ADR-034)
// Atomes UI partagés entre LogementCard, InfoFbzTab, SyncBeds24Tab,
// ServicesTab, ChecklistsTab, FormulairesTab, LivretTab.
// Extraits depuis l'ancien LogementsPanel.jsx + ajout helpers temporels.

import { forwardRef } from 'react'
import { DS } from "../../lib/ui"

export const lblStyle = {
  fontSize:10,color:"#888",fontWeight:700,marginBottom:3,
  textTransform:"uppercase",letterSpacing:.4,
}
export const inpStyle = {
  border:"1px solid #dde3ec",borderRadius:7,padding:"7px 10px",
  fontSize:12,outline:"none",background:"#f8fafd",
  fontFamily:DS.fontBody,width:"100%",
}

export function Section({ title, children, intro }) {
  return (
    <div style={{background:DS.white,borderRadius:12,padding:18,marginBottom:14,boxShadow:"0 2px 8px rgba(0,0,0,.06)"}}>
      <div style={{fontFamily:DS.fontDisplay,fontSize:15,fontWeight:700,color:DS.navy,marginBottom:intro ? 6 : 14}}>{title}</div>
      {intro && <div style={{fontSize:11,color:"#888",marginBottom:14,lineHeight:1.5}}>{intro}</div>}
      {children}
    </div>
  )
}

// SectionCard — carte de section différenciée (UI pure), mutualisée entre
// InfoFbzTab et LivretTab (FRONT 2). Extraite byte-identique d'InfoFbzTab
// (WAGON R Lot R2bis / R5 A1/A1bis). Différenciation par TROIS leviers cumulés :
// liseré latéral 5px (accent), fond de carte teinté (bg), bande de titre pleine
// largeur au fond accent léger (headerBg). `titleColor` optionnel : variante plus
// foncée et lisible sur fond clair (par défaut titleColor = accent).
//
// FRONT 2 — repliable optionnel : si `collapsible` est vrai, la bande de titre
// devient un bouton qui appelle `onToggle` ; le contenu est masqué quand
// `open === false`. Sans `collapsible`, comportement statique inchangé
// (compat InfoFbzTab : aucune des props collapsible/open/onToggle n'est fournie).
export function SectionCard({
  icon,
  title,
  subtitle,
  accent,
  titleColor,
  headerBg,
  bg,
  collapsible = false,
  open = true,
  onToggle,
  children,
}) {
  // Titre : le texte `{icon} {title}` est le SEUL contenu de ce div, et la
  // couleur y est portée — structure byte-identique à l'origine (InfoFbzTab),
  // pour que getByText(title) tombe sur ce div (parent = bande de titre). Le
  // chevron collapsible est un élément FRÈRE (hors du div titre) pour ne pas
  // polluer le texte du titre (les tests matchent le titre exact).
  const titleEl = (
    <div
      style={{
        fontFamily: DS.fontDisplay,
        fontSize: 15,
        fontWeight: 700,
        color: titleColor || accent,
        letterSpacing: 0.2,
      }}
    >
      {icon} {title}
    </div>
  )
  const headerInner = collapsible ? (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          aria-hidden="true"
          style={{
            fontSize: 11,
            transition: "transform .15s",
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
            display: "inline-block",
            color: titleColor || accent,
          }}
        >
          ▶
        </span>
        {titleEl}
      </div>
      {subtitle && (
        <div style={{ fontSize: 12, color: "#5a6675", marginTop: 3, lineHeight: 1.5 }}>
          {subtitle}
        </div>
      )}
    </>
  ) : (
    <>
      {titleEl}
      {subtitle && (
        <div style={{ fontSize: 12, color: "#5a6675", marginTop: 3, lineHeight: 1.5 }}>
          {subtitle}
        </div>
      )}
    </>
  )

  return (
    <div
      style={{
        background: bg,
        borderRadius: 12,
        marginBottom: 14,
        border: `1px solid ${DS.border}`,
        borderLeft: `5px solid ${accent}`,
        overflow: "hidden",
      }}
    >
      {collapsible ? (
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          style={{
            background: headerBg,
            padding: "12px 18px 11px 17px",
            borderBottom: `1px solid ${headerBg}`,
            border: "none",
            borderTop: "none",
            borderRight: "none",
            borderLeft: "none",
            width: "100%",
            textAlign: "left",
            cursor: "pointer",
            fontFamily: DS.fontBody,
          }}
        >
          {headerInner}
        </button>
      ) : (
        <div
          style={{
            background: headerBg,
            padding: "12px 18px 11px 17px",
            borderBottom: `1px solid ${headerBg}`,
          }}
        >
          {headerInner}
        </div>
      )}
      {open && <div style={{ padding: "16px 18px 18px 17px" }}>{children}</div>}
    </div>
  )
}

// Responsive Lot 0 : `fbz-row` se replie en 1 colonne sous md (768), 2 colonnes
// au-delà (colonnes portées par la classe, pas l'inline → mobile OK).
export function Row({ children }) {
  return <div className="fbz-row" style={{marginBottom:8}}>{children}</div>
}

export function Field({ label, children }) {
  return (
    <div>
      <div style={lblStyle}>{label}</div>
      {children}
    </div>
  )
}

export function FieldFull({ label, children }) {
  return (
    <div style={{marginBottom:10}}>
      <div style={lblStyle}>{label}</div>
      {children}
    </div>
  )
}

export function Inp({ value, onChange, placeholder, disabled, type = 'text' }) {
  return (
    <input type={type} value={value ?? ''} onChange={e => onChange?.(e.target.value)}
      placeholder={placeholder} disabled={disabled}
      style={{...inpStyle,background:disabled ? '#eef2f7' : '#f8fafd',color:disabled ? '#888' : '#444'}}/>
  )
}

export function Select({ value, onChange, options, disabled }) {
  return (
    <select value={value ?? ''} onChange={e => onChange?.(e.target.value)} disabled={disabled}
      style={{...inpStyle,background:disabled ? '#eef2f7' : '#f8fafd',cursor:disabled ? 'not-allowed' : 'pointer'}}>
      {options.map(opt => {
        const v = typeof opt === 'object' ? opt.value : opt
        const l = typeof opt === 'object' ? opt.label : opt
        return <option key={v} value={v}>{l}</option>
      })}
    </select>
  )
}

// PR #145 — forwardRef pour permettre l'insertion de variables au curseur
// (VariableInsertPanel a besoin d'un ref sur l'élément textarea natif).
export const Textarea = forwardRef(function Textarea({ value, onChange, placeholder, rows = 4 }, ref) {
  return (
    <textarea ref={ref} value={value ?? ''} onChange={e => onChange?.(e.target.value)}
      placeholder={placeholder} rows={rows}
      style={{...inpStyle,resize:"vertical",lineHeight:1.5}}/>
  )
})

// ── Helpers temporels (Phase 2.E.1) ───────────────────────────────

// 96 valeurs : '00:00', '00:15', '00:30', ..., '23:45'
export function generateTimes() {
  const out = []
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      out.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
    }
  }
  return out
}

// 24 valeurs : 15, 30, 45, ..., 360 (en minutes)
export function generateDurations() {
  const out = []
  for (let m = 15; m <= 360; m += 15) out.push(m)
  return out
}

// 24 valeurs : 'Pas de limite' + '01:00' → '23:00' (heure pleine)
export function generateDeadlineHours() {
  const out = ['Pas de limite']
  for (let h = 1; h < 24; h++) out.push(`${String(h).padStart(2, '0')}:00`)
  return out
}

// Range numérique inclus → array d'options { value, label } pour Select
export function rangeOptions(from, to) {
  const out = []
  for (let i = from; i <= to; i++) out.push({ value: i, label: String(i) })
  return out
}
